import { apiFetch, downloadFile } from "../../../lib/http";
import React, { useState, useEffect } from "react";
import { X, FileText, Download, Eye, ExternalLink } from "lucide-react";
import { Modal } from "../../../components/ui/Modal";

interface SourceRow {
  id: number;
  row_hash: string;
  source_table: string;
  uploaded_file_id: string;
  source_row_number?: number;
  source_sheet_name?: string;
  source_page_number?: number;
  original_name?: string;
  storage_path?: string;
  file_bank?: string;
  [key: string]: any;
}

interface FileInfo {
  id: string;
  original_name: string;
  storage_path: string;
  bank: string;
  mime_type?: string;
  uploaded_at?: string;
  value_date_min?: string;
  value_date_max?: string;
}

interface SourceRowsViewerProps {
  entryId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export function SourceRowsViewer({ entryId, isOpen, onClose }: SourceRowsViewerProps) {
  const [loading, setLoading] = useState(false);
  const [sourceRows, setSourceRows] = useState<SourceRow[]>([]);
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (isOpen && entryId != null) {
      loadSourceRows();
    } else {
      setSourceRows([]);
      setFiles([]);
      setPreviewFileId(null);
      setPreviewData(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entryId]);


  const loadSourceRows = async () => {
    if (!entryId || entryId === null) return;
    
    setLoading(true);
    try {
      const res = await apiFetch(`/api/entries/${entryId}/source-rows`);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }
      const data = await res.json();
      setSourceRows(data.source_rows || []);
      setFiles(data.files || []);
    } catch (e) {
      console.error("Errore caricamento righe source:", e);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewFile = async (fileId: string, rows?: SourceRow[]) => {
    setPreviewLoading(true);
    setPreviewFileId(fileId);
    
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) return;

      const ext = file.original_name.split('.').pop()?.toLowerCase();
      const params = new URLSearchParams();
      
      // Raccogli tutte le righe rilevanti per questo file
      const fileRows = rows || sourceRows.filter(r => r.uploaded_file_id === fileId);
      
      if (fileRows.length > 0) {
        const firstRow = fileRows[0];
        
        // Per Excel e CSV: passa tutte le righe rilevanti
        if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
          const rowNumbers = fileRows
            .map(r => r.source_row_number)
            .filter((n): n is number => n !== null && n !== undefined)
            .filter((n, idx, arr) => arr.indexOf(n) === idx); // rimuovi duplicati
          
          if (rowNumbers.length > 0) {
            params.append('row_numbers', rowNumbers.join(','));
          }
          
          if (firstRow.source_sheet_name) {
            params.append('sheet_name', firstRow.source_sheet_name);
          }
        }
        
        // Per PDF: passa il numero pagina
        if (ext === 'pdf' && firstRow.source_page_number) {
          params.append('page_number', String(firstRow.source_page_number));
        }
      }

      const res = await apiFetch(`/api/files/${fileId}/preview?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPreviewData(data);
    } catch (e) {
      console.error("Errore preview file:", e);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      await downloadFile(`/api/files/${fileId}/download`, fileName);
    } catch (e) {
      console.error("Errore download file:", e);
    }
  };

  const getBankLabel = (bank?: string) => {
    if (!bank) return "Sconosciuta";
    const b = bank.toLowerCase();
    if (b === "stonex") return "Stonex";
    if (b === "icbc") return "ICBC";
    if (b === "julius") return "Julius";
    return bank;
  };

  const getTableLabel = (table: string) => {
    if (table === "data_stonex") return "Stonex";
    if (table === "data_icbc") return "ICBC";
    if (table === "data_julius") return "Julius";
    return table;
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Righe Source - Entry #{entryId}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Caricamento...</div>
        ) : sourceRows.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Nessuna riga source trovata per questo entry.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Lista file */}
            {files.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">File Originali</h3>
                <div className="space-y-2">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {file.original_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getBankLabel(file.bank)} • {file.mime_type || "File"}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const fileRows = sourceRows.filter(r => r.uploaded_file_id === file.id);
                            handlePreviewFile(file.id, fileRows);
                          }}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="Anteprima"
                        >
                          <Eye className="h-4 w-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDownloadFile(file.id, file.original_name)}
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="Scarica"
                        >
                          <Download className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lista righe source */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Righe Source ({sourceRows.length})
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {sourceRows.map((row, idx) => {
                  const file = files.find(f => f.id === row.uploaded_file_id);
                  return (
                    <div
                      key={`${row.source_table}-${row.row_hash}-${idx}`}
                      className="p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              {getTableLabel(row.source_table)}
                            </span>
                            {file && (
                              <span className="text-xs text-gray-500">
                                {file.original_name}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            {(row.source_row_number || row.source_sheet_name || row.source_page_number) && (
                              <div className="flex items-center gap-2">
                                {row.source_row_number && (
                                  <span>Riga: {row.source_row_number}</span>
                                )}
                                {row.source_sheet_name && (
                                  <span>• Foglio: {row.source_sheet_name}</span>
                                )}
                                {row.source_page_number && (
                                  <span>• Pagina: {row.source_page_number}</span>
                                )}
                              </div>
                            )}
                            {row["Value Date"] && (
                              <div>
                                Value Date: {new Date(row["Value Date"]).toLocaleDateString("it-IT")}
                              </div>
                            )}
                            {row["Doc #"] && (
                              <div>Doc #: {row["Doc #"]}</div>
                            )}
                            {row["Trade Number"] && (
                              <div>Trade #: {row["Trade Number"]}</div>
                            )}
                            {row["Booking Text"] && (
                              <div className="truncate">Booking: {row["Booking Text"]}</div>
                            )}
                          </div>
                        </div>
                        {file && (
                          <button
                            onClick={() => {
                              const fileRows = sourceRows.filter(r => r.uploaded_file_id === file.id);
                              handlePreviewFile(file.id, fileRows);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                            title="Visualizza nel file originale"
                          >
                            <ExternalLink className="h-4 w-4 text-gray-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preview del file */}
            {previewFileId && (
              <div className="mt-6 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Anteprima File</h3>
                  <button
                    onClick={() => {
                      setPreviewFileId(null);
                      setPreviewData(null);
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Chiudi
                  </button>
                </div>
                {previewLoading ? (
                  <div className="text-center py-8 text-gray-500">Caricamento anteprima...</div>
                ) : previewData ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    {previewData.file_type === "pdf" && previewData.image_base64 && (
                      <div>
                        <div className="text-xs text-gray-600 mb-2">
                          Pagina {previewData.page_number} di {previewData.total_pages}
                        </div>
                        <img
                          src={previewData.image_base64}
                          alt={`Pagina ${previewData.page_number}`}
                          className="max-w-full h-auto border border-gray-200 rounded"
                        />
                      </div>
                    )}
                    {(previewData.file_type === "excel" || previewData.file_type === "csv") && (
                      <div>
                        <div className="text-xs text-gray-600 mb-2">
                          {previewData.sheet_name && `Foglio: ${previewData.sheet_name} • `}
                          {previewData.row_numbers && previewData.row_numbers.length > 0 && (
                            <span>Righe evidenziate: {previewData.row_numbers.join(", ")} • </span>
                          )}
                          {previewData.context_start_row && (
                            <span>Righe {previewData.context_start_row}-{previewData.context_end_row}</span>
                          )}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-xs border border-gray-300 bg-white">
                            <tbody>
                              {previewData.context?.map((row: any, idx: number) => {
                                const rowNum = previewData.context_start_row + idx;
                                const isHighlighted = previewData.highlighted_indices?.includes(rowNum) || 
                                                     previewData.row_numbers?.includes(rowNum);
                                return (
                                  <tr
                                    key={idx}
                                    className={isHighlighted ? "bg-yellow-100 font-medium" : ""}
                                  >
                                    {Object.entries(row).map(([key, cell]: [string, any], cellIdx: number) => {
                                      // Formatta le once con 3 decimali se la colonna contiene "Quantity" o "ounces"
                                      const isOuncesColumn = key.toLowerCase().includes('quantity') || key.toLowerCase().includes('ounces');
                                      let displayValue = cell !== null && cell !== undefined ? String(cell) : "";
                                      if (isOuncesColumn && cell !== null && cell !== undefined) {
                                        const numValue = parseFloat(cell);
                                        if (!isNaN(numValue)) {
                                          displayValue = numValue.toFixed(3);
                                        }
                                      }
                                      return (
                                        <td
                                          key={cellIdx}
                                          className={`px-2 py-1 border border-gray-200 ${isHighlighted ? "bg-yellow-100" : ""}`}
                                        >
                                          {displayValue}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Anteprima non disponibile
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

